import { Box, Button, Flex, Image, Input, Stack, Text } from '@chakra-ui/react'
import React, { useState } from 'react'
import microsoftLogo from '../assets/static/images/microsoft.png'
import { useAuth } from '../context/AuthContext'
import { CLIENT_CONFIG } from '../core/client/buildConfig'
import type { ClientConfig } from '../core/client/ClientProvider'
import { useLanguage } from '../hooks/utility/useLanguage'

const LoginPage: React.FC = () => {
  const { signIn, signInWithPassword } = useAuth()
  const { t } = useLanguage()
  // Static per-client switch (config.json), deliberately not a runtime flag.
  const customLoginEnabled = (CLIENT_CONFIG as ClientConfig).auth?.custom_login === true
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const loginLogoUrl = (CLIENT_CONFIG as ClientConfig).theme.branding.loginLogoUrl
  const loginCardOffsetY = (CLIENT_CONFIG as ClientConfig).theme.layout.loginCardOffsetY
  const loginCardTransform = loginCardOffsetY
    ? { base: `translateY(${loginCardOffsetY.base})`, md: `translateY(${loginCardOffsetY.md})` }
    : undefined

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)
    try {
      await signInWithPassword(username, password)
    } catch (err: any) {
      setError(err.message || 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Flex
      flexDirection='column'
      width='100vw'
      height='100vh'
      bgImage={
        CLIENT_CONFIG.theme.branding.backgroundImage
          ? `url(${CLIENT_CONFIG.theme.branding.backgroundImage})`
          : undefined
      }
      bg={!CLIENT_CONFIG.theme.branding.backgroundImage ? CLIENT_CONFIG.theme.colors.primary : undefined}
      bgSize='cover'
      bgPosition='center'
      bgRepeat='no-repeat'
      overflow='hidden'
      justifyContent='center'
      alignItems='center'
      position='relative'
    >
      {loginLogoUrl && (
        <Image
          src={loginLogoUrl}
          alt={CLIENT_CONFIG.theme.branding.companyName}
          position='absolute'
          top={{ base: 8, md: 12 }}
          left='50%'
          transform='translateX(-50%)'
          maxH={{ base: '72px', md: '130px' }}
          maxW='70%'
          objectFit='contain'
        />
      )}
      <Stack spacing={4} alignItems='center' width={{ base: '85%', md: '360px' }} transform={loginCardTransform}>
        <Button
          leftIcon={<Image src={microsoftLogo} alt='Microsoft logo' boxSize='22px' />}
          iconSpacing={3}
          borderRadius='full'
          height='56px'
          width='full'
          fontSize='lg'
          fontWeight='600'
          onClick={signIn}
          // Microsoft's sign-in branding: white surface, dark neutral text, so the
          // button reads the same on any client background photo or primary color.
          color='gray.800'
          bg='white'
          boxShadow='0 8px 24px rgba(0, 0, 0, 0.18)'
          _hover={{ bg: 'gray.50', boxShadow: '0 10px 28px rgba(0, 0, 0, 0.24)' }}
          _active={{ bg: 'gray.100' }}
        >
          {t('auth.loginWithMicrosoft')}
        </Button>

        {customLoginEnabled && (
          <>
            <Text
              fontSize='sm'
              textAlign='center'
              cursor='pointer'
              textDecoration='underline'
              onClick={() => setShowPasswordForm(!showPasswordForm)}
              color='white'
              textShadow='0 1px 3px rgba(0, 0, 0, 0.5)'
              _hover={{ color: 'gray.300' }}
            >
              {showPasswordForm ? t('auth.hidePasswordForm') : t('auth.loginWithPassword')}
            </Text>

            {showPasswordForm && (
              <Box as='form' onSubmit={handlePasswordLogin} width='full'>
                <Stack spacing={3}>
                  <Input
                    placeholder='Username'
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    required
                    bg='white'
                    borderRadius='full'
                  />
                  <Input
                    type='password'
                    placeholder='Password'
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    bg='white'
                    borderRadius='full'
                  />
                  {error && (
                    <Text color='red.300' fontSize='sm' textAlign='center'>
                      {error}
                    </Text>
                  )}
                  <Button
                    type='submit'
                    width='full'
                    borderRadius='full'
                    isLoading={isLoading}
                    variant='solid'
                    colorScheme='primaryScheme'
                  >
                    {t('auth.login')}
                  </Button>
                </Stack>
              </Box>
            )}
          </>
        )}
      </Stack>
    </Flex>
  )
}

export default LoginPage
